"use client";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Parallax } from "react-scroll-parallax";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import CustomImage from "@/components/shared/CustomImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { Home, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

const HeroSlide = ({ home }: { home: PayLoadData<Home> | undefined }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { language } = useLanguage();
  const { t } = useTranslation();
  const slideData = useMemo(
    () => home?.[language]?.heroSliders?.slide || [],
    [home, language]
  );

  useEffect(() => {
    if (slideData && slideData?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) =>
          prev === slideData.length - 1 ? 0 : prev + 1
        );
      }, 4000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [language, slideData]);

  if (slideData && slideData.length === 0) {
    return <div className="text-center text-white">No slides available.</div>;
  }

  const fadeVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };

  return (
    <section
      className="relative size-full bg-primary-500 max-md:pb-4 max-md:pt-0 sm:min-h-screen md:h-screen md:overflow-hidden xl:h-screen"
      id="hero"
    >
      <div className="pointer-events-none absolute inset-0 z-10 h-1/2 bg-gradient-to-b from-primary-800 to-transparent to-30%" />

      {/* Desktop */}
      <div className="hidden md:block">
        {/* Slides */}
        <div className="absolute inset-0">
          <Swiper
            effect="slide"
            slidesPerView={1}
            speed={1000}
            pagination={{ clickable: true }}
            autoplay={{
              delay: 3000,
            }}
            modules={[Pagination, Autoplay]}
            loop
            className="custom-pagination light size-full"
          >
            {slideData.map((item, id) => (
              <SwiperSlide key={id} className="overflow-hidden">
                <Parallax
                  key={id}
                  className="absolute inset-0 -inset-y-4 transition-opacity duration-1000"
                  speed={-200}
                >
                  <CustomImage
                    src={getImageUrl(item?.background)}
                    alt={
                      (item?.background as Media).alt || "image pension hohenau"
                    }
                    fill
                    className="object-cover object-center"
                    priority
                  />
                </Parallax>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Content */}
        <div className="pointer-events-none relative z-10 flex h-full items-center justify-center px-5">
          <div className="container p-0 pt-16">
            <div className="flex max-w-md items-center justify-center gap-20">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  className="bg-primary-500/85 p-10 text-white"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={fadeVariants}
                >
                  <div className="relative size-36 md:size-28 xl:size-48">
                    <CustomImage
                      src={"/logo_site.svg"}
                      alt="Logo"
                      fill
                      className="mb-20"
                      priority
                    />
                  </div>
                  <h1 className="mb-6 text-4xl font-bold">
                    {slideData?.[currentSlide]?.title}
                  </h1>
                  <p className="mb-8 text-xl text-white/90">
                    {slideData?.[currentSlide]?.subtitle}
                  </p>
                  <div className="pointer-events-auto flex flex-col gap-4 sm:flex-row">
                    <motion.div>
                      <Link
                        href="/booking"
                        className="group inline-flex items-center justify-center rounded-full border border-white bg-white/10 px-6 py-4 font-medium text-white transition-transform duration-500"
                      >
                        <span>
                          {t({ de: "Jetzt Anfragen", en: "Book now" })}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="block md:hidden">
        <Swiper
          effect="slide"
          slidesPerView={1}
          speed={1000}
          pagination={{ clickable: true }}
          autoplay={{
            delay: 3000,
          }}
          modules={[Pagination, Autoplay]}
          loop
          className="custom-pagination pagination-no-absolute light"
        >
          {slideData.map((item, id) => (
            <SwiperSlide key={id} className="">
              <div className="flex flex-col">
                {/* img */}
                <div className="relative aspect-square w-full">
                  <CustomImage
                    src={getImageUrl(item?.background)}
                    alt={
                      (item?.background as Media).alt || "image pension hohenau"
                    }
                    fill
                    className="object-cover object-center"
                    priority
                  />
                </div>

                {/* content */}
                <div className="flex-1 bg-primary-500 px-5">
                  <h1 className="mb-6 mt-12 text-4xl font-bold text-white">
                    {item?.title}
                  </h1>
                  <p className="text-xl text-white/90">{item?.subtitle}</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default HeroSlide;
